function generateCacheKey(args: readonly unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg !== 'object' || arg === null) return String(arg)

      const sortedEntries = Object.entries(arg).toSorted(([left], [right]) =>
        left.localeCompare(right),
      )
      return JSON.stringify(Object.fromEntries(sortedEntries)) ?? String(arg)
    })
    .join('|')
}

export function memoize<Args extends readonly unknown[], Result>(
  fn: (...args: Args) => Result,
): (...args: Args) => Result {
  const cache = new Map<string, { value: Result }>()

  const memoized = (...args: Args): Result => {
    // In tests, we don't want to use the cache
    if (process.env.NODE_ENV === 'test') {
      return fn(...args)
    }

    const key = generateCacheKey(args)

    const cachedResult = cache.get(key)
    if (cachedResult) {
      return cachedResult.value
    }

    const result = fn(...args)
    cache.set(key, { value: result })
    return result
  }

  return memoized
}
